import React, { useCallback, useMemo, useState } from "react";

type TranslationEditorProps = {
  initialValue: string | null;
  onSave: (value: string | null) => Promise<void> | void;
};

const PLACEHOLDER_REGEX = /%(\d+\$)?[dsfbx]/g;
const EDITING_KEY = "self";

const extractPlaceholders = (str: string): string[] => {
  return str.match(PLACEHOLDER_REGEX) || [];
};

const renderHighlightedText = (
  text: string,
  missingPlaceholders: string[] = []
): React.ReactNode[] => {
  if (!PLACEHOLDER_REGEX.test(text)) return [text];
  PLACEHOLDER_REGEX.lastIndex = 0;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(PLACEHOLDER_REGEX)) {
    const placeholder = match[0];
    const start = match.index ?? 0;
    const end = start + placeholder.length;
    const isMissing = missingPlaceholders.includes(placeholder);

    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    parts.push(
      <span
        key={`${start}-${placeholder}`}
        className={
          isMissing
            ? "rounded px-1 bg-red-500/20"
            : "rounded px-1 bg-blue-500/20"
        }
      >
        {placeholder}
      </span>
    );

    lastIndex = end;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

export function TranslationEditor({
  initialValue,
  onSave,
}: TranslationEditorProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [originalEditValue, setOriginalEditValue] = useState<string>("");
  const [missingPlaceholder, setMissingPlaceholder] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEdit = useCallback(() => {
    setEditingId(EDITING_KEY);
    setEditValue(initialValue || "");
    setOriginalEditValue(initialValue || "");
    setMissingPlaceholder([]);
  }, [initialValue]);

  const updateEditValue = useCallback(
    (value: string) => {
      const originalPlaceholders = extractPlaceholders(originalEditValue);
      const newPlaceholders = extractPlaceholders(value);

      const missing = originalPlaceholders.filter(
        (ph) => !newPlaceholders.includes(ph)
      );

      setMissingPlaceholder(missing);
      setEditValue(value);
    },
    [originalEditValue]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditValue("");
    setMissingPlaceholder([]);
  }, []);

  const handleSave = useCallback(async () => {
    if (missingPlaceholder.length > 0) return;

    try {
      setIsSaving(true);
      await onSave(editValue || null);
      setEditingId(null);
      setEditValue("");
      setMissingPlaceholder([]);
    } finally {
      setIsSaving(false);
    }
  }, [editValue, missingPlaceholder.length, onSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void handleSave();
      } else if (e.key === "Escape") {
        handleCancelEdit();
      }
    },
    [handleSave, handleCancelEdit]
  );

  const canSaveEdit = missingPlaceholder.length === 0;
  const isEditing = editingId === EDITING_KEY;

  const highlightedEditValue = useMemo(
    () => renderHighlightedText(editValue || " ", missingPlaceholder),
    [editValue, missingPlaceholder]
  );

  const highlightedViewValue = useMemo(
    () => renderHighlightedText(initialValue || ""),
    [initialValue]
  );

  return (
    <div className="text-sm">
      {isEditing ? (
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <div className="relative w-full">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 whitespace-pre-wrap break-words rounded-sm border border-border bg-field py-1.5 px-2 text-sm text-onfield"
              >
                {highlightedEditValue}
              </div>

              <textarea
                value={editValue}
                onChange={(e) => updateEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="relative z-10 block w-full resize-none rounded-sm border border-border bg-transparent py-1.5 px-2 text-sm text-transparent caret-onfield placeholder:text-onfield/40 focus:border-primary focus:ring-primary"
                rows={2}
                autoFocus
              />
            </div>

            {!canSaveEdit && (
              <div className="mt-1 text-xs text-red-600">
                Missing placeholder{missingPlaceholder.length > 1 ? "s" : ""}:{" "}
                {missingPlaceholder.join(", ")}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isSaving || !canSaveEdit}
              className="rounded bg-primary p-1.5 text-xs font-medium text-onprimary hover:bg-primary-darker disabled:opacity-50"
              aria-label="Save"
            >
              {isSaving ? (
                <span className="inline-block size-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <svg
                  className="size-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </button>

            <button
              type="button"
              onClick={handleCancelEdit}
              className="rounded bg-surface-darker p-1.5 text-xs font-medium text-onsurface hover:bg-surface-darker/80"
              aria-label="Cancel"
            >
              <svg
                className="size-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleStartEdit}
          className={`w-full rounded-sm border border-transparent py-1.5 px-2 text-left transition-colors hover:border-border hover:bg-field ${
            initialValue === null ? "italic text-onsurface/40" : "text-onsurface"
          }`}
        >
          {initialValue ? (
            <span className="whitespace-pre-wrap break-words">
              {highlightedViewValue}
            </span>
          ) : (
            "Empty"
          )}
        </button>
      )}
    </div>
  );
}